import jsPDF from "jspdf";
import { ensureArabicFont, pdfText, hasArabic } from "@/lib/pdfFonts";
import { pdfStr } from "@/lib/pdfStrings";
import { fmtMoney } from "@/lib/money";
import { drawHeader, drawFooter, hexToRgb } from "@/lib/invoicePdf";

/** Account statement (client / supplier / branch / general) in ES · EN · AR. */
export async function statementPDF(st, lang = "es", settings = {}) {
  const S = pdfStr(lang);
  const estilo = settings.estilo || {};
  const emisor = st.emisor || settings.empresa || {};
  const currency = st.moneda || "EUR";
  const money = (v) => fmtMoney(v, currency);

  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const brand = hexToRgb(estilo.color);

  const dynamic = [st.titulo, emisor.razon_social, emisor.direccion, ...(st.movimientos || []).map((m) => m.concepto)];
  if (lang === "ar" || dynamic.some(hasArabic)) await ensureArabicFont(doc);

  const period = [st.desde, st.hasta].filter(Boolean).join("  →  ") || S.all_dates;
  let y = await drawHeader(doc, {
    title: S.statement_title,
    subtitle: `${S[`statement_of_${st.tipo}`] || ""}: ${st.titulo || "—"}      ${S.period}: ${period}`,
    emisor, estilo, lang,
  });

  // KPI strip
  const kpis = [
    [S.opening_balance, money(st.saldo_inicial)],
    [S.total_charges, money(st.total_cargos)],
    [S.total_credits, money(st.total_abonos)],
    [S.closing_balance, money(st.saldo_final)],
  ];
  const bw = (W - 64) / 4;
  kpis.forEach(([label, value], i) => {
    const x = 32 + i * bw;
    const last = i === 3;
    if (last) doc.setFillColor(brand[0], brand[1], brand[2]);
    else doc.setFillColor(248, 250, 252);
    doc.rect(x, y - 12, bw - 6, 44, "F");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(last ? 255 : 100, last ? 255 : 116, last ? 255 : 139);
    pdfText(doc, label.toUpperCase(), x + 8, y + 2);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(last ? 255 : 15, last ? 255 : 23, last ? 255 : 42);
    pdfText(doc, value, x + 8, y + 20);
  });
  y += 52;

  const drawTableHead = () => {
    doc.setFillColor(11, 15, 25);
    doc.rect(32, y, W - 64, 20, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    pdfText(doc, S.date, 40, y + 14);
    pdfText(doc, S.movement, 108, y + 14);
    pdfText(doc, S.reference, 190, y + 14);
    pdfText(doc, S.description, 280, y + 14);
    pdfText(doc, S.charge, W - 190, y + 14, { align: "right" });
    pdfText(doc, S.credit, W - 118, y + 14, { align: "right" });
    pdfText(doc, S.balance, W - 42, y + 14, { align: "right" });
    y += 20;
  };
  drawTableHead();

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  (st.movimientos || []).forEach((m, i) => {
    if (y > H - 90) {
      drawFooter(doc, { estilo, lang });
      doc.addPage();
      y = 56;
      drawTableHead();
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
    }
    if (i % 2 === 0) {
      doc.setFillColor(248, 250, 252);
      doc.rect(32, y, W - 64, 18, "F");
    }
    doc.setTextColor(30, 41, 59);
    pdfText(doc, m.fecha || "", 40, y + 12);
    pdfText(doc, S[`mov_${m.tipo}`] || m.tipo || "", 108, y + 12, { maxWidth: 78 });
    pdfText(doc, m.referencia || "—", 190, y + 12, { maxWidth: 86 });
    pdfText(doc, m.concepto || "—", 280, y + 12, { maxWidth: W - 480 });
    pdfText(doc, m.cargo ? money(m.cargo) : "—", W - 190, y + 12, { align: "right" });
    pdfText(doc, m.abono ? money(m.abono) : "—", W - 118, y + 12, { align: "right" });
    doc.setFont("helvetica", "bold");
    pdfText(doc, money(m.saldo), W - 42, y + 12, { align: "right" });
    doc.setFont("helvetica", "normal");
    y += 18;
  });

  if (!(st.movimientos || []).length) {
    doc.setTextColor(148, 163, 184);
    pdfText(doc, S.no_movements, W / 2, y + 24, { align: "center" });
    y += 40;
  }

  y += 10;
  doc.setDrawColor(brand[0], brand[1], brand[2]);
  doc.setLineWidth(1.2);
  doc.line(W - 250, y, W - 32, y);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11.5);
  doc.setTextColor(30, 41, 59);
  pdfText(doc, S.closing_balance, W - 160, y + 18, { align: "right" });
  pdfText(doc, money(st.saldo_final), W - 42, y + 18, { align: "right" });

  drawFooter(doc, { estilo, lang });
  doc.save(`statement-${(st.titulo || "general").replace(/\s+/g, "_")}.pdf`);
}
